/**
 * SlotSelector Component
 *
 * Displays a visual selector for choosing a profile image slot.
 * Shows slot options with icons, aspect ratio indicators, and
 * highlights the suggested slot based on uploaded image dimensions.
 */

import { cn } from '@pray/shared/utils/styles';

import { PROFILE_SLOTS, type SlotType, type SlotSuggestion } from '../../utils/imageUtils';

import styles from './SlotSelector.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface SlotSelectorProps {
  /** Called when user selects a slot */
  onSelect: (slot: SlotType) => void;
  /** Suggested slot based on image ratio (will be highlighted) */
  suggestion?: SlotSuggestion | null;
  /** Currently selected slot */
  selectedSlot?: SlotType | null;
  /** Whether selection is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Slot Icons
// ============================================================================

function VerticalIcon() {
  return (
    <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="22" height="30" rx="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function SquareIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="26" height="26" rx="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function HorizontalIcon() {
  return (
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="30" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function CircleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="14" r="13" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function CoverIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="26" height="26" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="9" cy="9" r="2" fill="currentColor" />
      <path d="M27 18L21 12L11 22H27V18Z" fill="currentColor" opacity="0.5" />
    </svg>
  );
}

const SLOT_ICONS: Record<SlotType, React.ComponentType> = {
  profile_vertical_image: VerticalIcon,
  profile_image: SquareIcon,
  profile_horizontal_image: HorizontalIcon,
  profile_circle_image: CircleIcon,
  default_cover_art: CoverIcon,
};

// ============================================================================
// Component
// ============================================================================

export function SlotSelector({ onSelect, suggestion, selectedSlot, disabled = false, className }: SlotSelectorProps) {
  const slots: SlotType[] = [
    'profile_vertical_image',
    'profile_image',
    'profile_horizontal_image',
    'profile_circle_image',
    'default_cover_art',
  ];

  return (
    <div className={cn(styles.container, disabled && styles.disabled, className)}>
      <p className={styles.title}>Which profile slot would you like to use?</p>

      <div className={styles.grid}>
        {slots.map((slot) => {
          const config = PROFILE_SLOTS[slot];
          const Icon = SLOT_ICONS[slot];
          const isSuggested = suggestion?.slot === slot;
          const isSelected = selectedSlot === slot;

          return (
            <button
              key={slot}
              type="button"
              className={cn(styles.slotButton, isSuggested && styles.suggested, isSelected && styles.selected)}
              onClick={() => onSelect(slot)}
              disabled={disabled}
            >
              <div className={styles.iconContainer}>
                <Icon />
              </div>
              <span className={styles.label}>{config.shortLabel}</span>
              {isSuggested && <span className={styles.badge}>Best fit</span>}
            </button>
          );
        })}
      </div>

      {suggestion && suggestion.cropRequired && (
        <p className={styles.cropWarning}>Note: Image will be cropped to fit the selected aspect ratio.</p>
      )}

      <div className={styles.actions}>
        {suggestion && (
          <button
            type="button"
            className={styles.useSuggestedButton}
            onClick={() => onSelect(suggestion.slot)}
            disabled={disabled}
          >
            Use {PROFILE_SLOTS[suggestion.slot].shortLabel} (suggested)
          </button>
        )}
      </div>
    </div>
  );
}

export default SlotSelector;
